import { useParams } from 'wouter'
import { useMutation } from '@tanstack/react-query'

import {
  cadVersionUploadedAsync,
  createCadVersion,
  uploadFile,
} from '@/lib/api/client'
import { useUpdateCadVersion } from '@/services/queries/cad_versions'
import { useCADQuery } from '@/services/queries/cads'

import { useToast } from '@/components/ui/use-toast'
import { useAppStore } from '@/state'
import { RawAssemblyTree } from '@/state'
import { compressFile } from '@/lib/gzip'
import CustomError from '@/lib/api/CustomError'

export const useAssemblyTreeQuery = () => {
  const { isLoading, data: cadData } = useCADQuery()
  const assemblyTree = cadData.rawAssemblyTree
  return {
    isLoading,
    data: {
      assemblyTree,
    },
  }
}

export const useCreateCadVersionMutation = () => {
  const { projectId, documentId } = useParams<{
    projectId: string
    documentId: string
  }>()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      cadId,
      comment,
      file,
    }: {
      cadId: string
      comment: string
      file: File
    }) => {
      try {
        const compressedFile = await compressFile(file)
        const cadVersion = await createCadVersion(cadId, {
          filename: compressedFile.name,
          comment,
        })

        const uploadUrl = cadVersion.upload_url
        if (!uploadUrl) {
          throw new Error('Failed to get upload URL')
        }
        if (!cadVersion.id) {
          throw new Error('Failed to get cad version ID')
        }
        await uploadFile(uploadUrl, compressedFile)
        await cadVersionUploadedAsync(cadVersion.id)
        return { cadVersion, cadId }
      } catch (e: any) {
        if (e instanceof CustomError) {
          toast({
            title: 'Error',
            description: e.message,
            variant: 'destructive',
          })
        }
        throw e
      }
    },
    onSuccess: ({ cadVersion }) => {
      window.location.href = `/p/${projectId}/document/${documentId}/cad?cv=${cadVersion.id}`
    },
  })
}

export const useUpdateCadVersionMutation = (props?: {
  preventViewWorkerKick?: boolean
  onSettled?: () => void
}) => {
  const { toast } = useToast()
  const viewWorker = useAppStore((state) => state.viewWorker)
  const { mutateAsync: updateCadVersion } = useUpdateCadVersion()

  return useMutation({
    mutationFn: async ({
      cadVersionId,
      documentVersionId,
      assemblyTree,
    }: {
      cadVersionId: string
      documentVersionId: string
      assemblyTree: RawAssemblyTree
    }) => {
      await updateCadVersion({
        cadVersionId,
        values: {
          assembly_tree: assemblyTree,
        },
      })

      return { cadVersionId, documentVersionId }
    },
    onSuccess: async ({ cadVersionId, documentVersionId }) => {
      if (
        !props?.preventViewWorkerKick &&
        viewWorker &&
        cadVersionId &&
        documentVersionId
      ) {
        await viewWorker.kick(cadVersionId, documentVersionId)
      }
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'Uh oh! Something went wrong.',
        description: 'There was a problem with your request.',
      })
    },
    onSettled: () => {
      if (typeof props?.onSettled === 'function') {
        props.onSettled()
      }
    },
  })
}
